import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { domain } from '../common/domain';

const BillingReport = () => {
  const [fromDate, setFromDate] = useState(() => {
    const today = new Date();
    return today;
  });
  const [toDate, setToDate] = useState(() => {
    const today = new Date();
    return today;
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalSales: 0,
    totalProfit: 0,
    totalCommission: 0,
    totalItems: 0
  });

  // Check URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const startDate = params.get('start_date');
    const endDate = params.get('end_date');

    if (startDate) setFromDate(new Date(startDate));
    if (endDate) setToDate(new Date(endDate));
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const fDate = fromDate ? format(fromDate, 'yyyy-MM-dd') : '';
      const tDate = toDate ? format(toDate, 'yyyy-MM-dd') : '';

      const response = await axios.get(domain() + '?endpoint=bills', {
        params: {
          start_date: fDate,
          end_date: tDate,
          type: 'product'
        },
      });

      setData(response.data.reports || []);
      
      // Calculate summary
      const stats = response.data.reports.reduce((acc, item) => ({
        totalSales: acc.totalSales + (parseFloat(item.total_amount) || 0),
        totalProfit: acc.totalProfit + (parseFloat(item.profit_amount) || 0),
        totalCommission: acc.totalCommission + (parseFloat(item.commission_amount) || 0),
        totalItems: acc.totalItems + 1
      }), { totalSales: 0, totalProfit: 0, totalCommission: 0, totalItems: 0 });

      setSummary(stats);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      setData([]);
      setSummary({ totalSales: 0, totalProfit: 0, totalCommission: 0, totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, [fromDate, toDate]);

  const formatNumber = (num, isAmount = false) => {
    const formatted = parseFloat(num).toFixed(2);
    return isAmount ? '₹' + formatted : formatted;
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-4">Billing Report</h3>
      
      {/* Summary Cards */}
      {data.length > 0 && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <h5 className="card-title">Total Sales</h5>
                <h3>₹{summary.totalSales.toFixed(2)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <h5 className="card-title">Total Profit</h5>
                <h3>₹{summary.totalProfit.toFixed(2)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <h5 className="card-title">Total Commission</h5>
                <h3>₹{summary.totalCommission.toFixed(2)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body">
                <h5 className="card-title">Total Items</h5>
                <h3>{summary.totalItems}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Filters */}
      <div className="row g-3 my-3">
        <div className="col-md-3 d-flex flex-column">
          <label>From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="dd-MM-yyyy"
            className="form-control"
            placeholderText="Select from date"
          />
        </div>
        <div className="col-md-3 d-flex flex-column">
          <label>To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="dd-MM-yyyy"
            className="form-control"
            placeholderText="Select to date"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="table-responsive mt-3">
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <table className="table table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>Product Name</th>
                <th>Rate</th>
                <th>Weight</th>
                <th>Total Amount</th>
                <th>Profit Amount</th>
                <th>Salesperson</th>
                <th>Commission</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No data found for the selected period
                  </td>
                </tr>
              ) : (
                data.map((item, i) => (
                  <tr key={i}>
                    <td>{item.product_name}</td>
                    <td>{formatNumber(item.rate, true)}</td>
                    <td>{item.weight}g</td>
                    <td>{formatNumber(item.total_amount, true)}</td>
                    <td>{formatNumber(item.profit_amount, true)}</td>
                    <td>{item.salesperson_name}</td>
                    <td>{formatNumber(item.commission_amount, true)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BillingReport; 